import { Icon } from '@iconify/react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Shared/Accordion/Accordion';
import { Switch } from '../../Shared/Switch/Switch';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/Domain/components/Shared/Form/Form';
import { RemoteMcpForm } from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpActionForm.hook';

type Props = {
  form: UseFormReturn<RemoteMcpForm>;
  isHidden: boolean;
  name: 'allowed_tools';
};

export const RemoteMcpActionTools = ({ form, isHidden, name }: Props) => {
  const { t } = useTranslation('remote_mcp');

  const { fields: tools, update } = useFieldArray({ control: form.control, name });
  const handleToggle = (idx: number, check: boolean) => {
    const currentItem = form.getValues(`${name}.${idx}`);
    update(idx, {
      ...currentItem,
      is_active: check,
    });
  };
  return (
    <div className="space-y-2" hidden={isHidden}>
      <Accordion
        type="single"
        collapsible
        className="w-full"
        data-testid="knowledge-base-action-sheet-advanced-options-accordion"
      >
        <AccordionItem
          value="connection_details"
          className="rounded-xl border border-border py-2 px-6"
        >
          <AccordionTrigger className="text-foreground font-medium px-1">
            <div className="flex items-center gap-3">
              <Icon icon="mingcute:settings-2-line" /> {t('form.advanced_settings')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="border-t pt-4 flex flex-col gap-10 px-1">
            <FormField
              name="default_tool_is_active"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <FormLabel>{t('form.default_tool_is_active_label')}</FormLabel>
                      <FormDescription>
                        {t('form.default_tool_is_active_description')}
                      </FormDescription>
                    </div>
                    <FormControl className="self-center">
                      <Switch
                        size="sm"
                        checked={field.value}
                        onCheckedChange={(check) => field.onChange(check)}
                      />
                    </FormControl>
                  </div>
                </FormItem>
              )}
            />
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {tools.map((tool, idx) => (
                <section key={tool.id} className="rounded-lg border bg-card p-4 shadow-sm">
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    <FormField
                      name={`${name}.${idx}.name`}
                      render={({ field }) => (
                        <FormItem className="flex-1 min-w-0 space-y-1">
                          <p className="text-xs font-medium text-foreground-subtle uppercase tracking-wide">
                            Tool
                          </p>
                          <FormControl>
                            <div className="flex w-full min-w-0 items-center rounded-md border border-border bg-muted px-2.5 py-1">
                              <span className="block w-full truncate break-words text-sm text-muted-foreground">
                                {field.value}
                              </span>
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      name={`${name}.${idx}.is_active`}
                      render={({ field }) => (
                        <FormItem className="flex flex-shrink-0 items-center gap-2">
                          <div className="text-right">
                            <p className="text-xs font-medium text-muted-foreground">Enabled</p>
                            <p className="text-[11px] text-foreground-subtle">
                              Allow this tool to run
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              size="sm"
                              checked={field.value ?? false}
                              onCheckedChange={(check) => {
                                field.onChange(check);
                                handleToggle(idx, check);
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </section>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
